import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Disc3, Plus, Trash2, Edit3, Save, X, Upload, Music, Mail,
  Loader2, Check, ChevronDown, ChevronUp, UserPlus, Users
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ──────────────────────────────────────────────────────────────────
   AlbumPacksTab — manage album packs and their stems (admin)
   ────────────────────────────────────────────────────────────────── */
export default function AlbumPacksTab({ adminPwd }) {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // id of expanded pack
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showEntitlementsFor, setShowEntitlementsFor] = useState(null);

  const fetchPacks = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/album-packs?password=${encodeURIComponent(adminPwd)}`);
      const data = await r.json();
      setPacks(Array.isArray(data) ? data : []);
    } catch {
      setPacks([]);
    } finally {
      setLoading(false);
    }
  }, [adminPwd]);

  useEffect(() => { fetchPacks(); }, [fetchPacks]);

  const handleDelete = async (pack) => {
    if (!window.confirm(`Supprimer l'album pack "${pack.name}" et tous ses stems ? Cette action est irreversible.`)) return;
    const r = await fetch(`${API}/admin/album-packs/${pack.id}?password=${encodeURIComponent(adminPwd)}`, { method: 'DELETE' });
    if (r.ok || r.status === 204) {
      setPacks(packs.filter(p => p.id !== pack.id));
      if (expanded === pack.id) setExpanded(null);
    } else {
      alert('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6" data-testid="album-packs-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-black text-white flex items-center gap-2">
            <Disc3 className="text-yellow-400" size={22} /> Album Packs (Stems)
          </h2>
          <p className="text-zinc-500 text-sm mt-1">
            Chaque pack vendu donne à l'acheteur l'acces aux stems dans le DAW.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          data-testid="album-pack-add-button"
          className="px-4 py-2 bg-yellow-500 text-black font-bold rounded-xl flex items-center gap-2 hover:bg-yellow-400">
          <Plus size={16} /> Nouveau pack
        </button>
      </div>

      <AnimatePresence>
        {showAdd && (
          <AlbumPackForm adminPwd={adminPwd} onClose={() => setShowAdd(false)} onSaved={(p) => { setPacks([p, ...packs]); setShowAdd(false); }} />
        )}
      </AnimatePresence>

      {loading ? (
        <p className="text-zinc-500 text-center py-8 flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={16} /> Chargement…
        </p>
      ) : packs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-700 p-8 text-center text-zinc-500">
          Aucun album pack pour le moment. Crée ton premier pack pour vendre des stems.
        </div>
      ) : (
        <div className="space-y-3">
          {packs.map(pack => (
            <div key={pack.id} className="rounded-xl border border-zinc-800 bg-zinc-900/50" data-testid={`album-pack-${pack.id}`}>
              {editingId === pack.id ? (
                <AlbumPackForm
                  adminPwd={adminPwd}
                  pack={pack}
                  onClose={() => setEditingId(null)}
                  onSaved={(updated) => { setPacks(packs.map(p => p.id === updated.id ? updated : p)); setEditingId(null); }}
                />
              ) : (
                <div className="flex items-center gap-3 p-4">
                  {pack.cover_image ? (
                    <img src={pack.cover_image} alt={pack.name} className="w-14 h-14 rounded-lg object-cover" />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
                      <Disc3 size={22} className="text-yellow-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-bold truncate">{pack.name}</h3>
                    <p className="text-xs text-zinc-500 truncate">
                      {pack.artist || '—'} • {pack.stems_count || 0} stems • {pack.price?.toFixed(2) || '0.00'}€
                      {!pack.active && <span className="ml-2 text-red-400 font-bold">INACTIF</span>}
                    </p>
                  </div>
                  <button
                    onClick={() => setExpanded(expanded === pack.id ? null : pack.id)}
                    data-testid={`album-pack-toggle-${pack.id}`}
                    className="px-3 py-1.5 bg-yellow-500/10 text-yellow-400 rounded-lg text-xs font-bold flex items-center gap-1 hover:bg-yellow-500/20">
                    {expanded === pack.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Stems
                  </button>
                  <button
                    onClick={() => setShowEntitlementsFor(showEntitlementsFor === pack.id ? null : pack.id)}
                    data-testid={`album-pack-buyers-${pack.id}`}
                    className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 rounded-lg text-xs font-bold flex items-center gap-1 hover:bg-emerald-500/20">
                    <Users size={14} /> Acheteurs
                  </button>
                  <button onClick={() => setEditingId(pack.id)} data-testid={`album-pack-edit-${pack.id}`} className="text-zinc-400 hover:text-yellow-400 p-1.5">
                    <Edit3 size={16} />
                  </button>
                  <button onClick={() => handleDelete(pack)} data-testid={`album-pack-delete-${pack.id}`} className="text-zinc-400 hover:text-red-400 p-1.5">
                    <Trash2 size={16} />
                  </button>
                </div>
              )}

              <AnimatePresence>
                {expanded === pack.id && editingId !== pack.id && (
                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden border-t border-zinc-800">
                    <StemsManager adminPwd={adminPwd} pack={pack} onCountChange={(n) => setPacks(packs.map(p => p.id === pack.id ? { ...p, stems_count: n } : p))} />
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {showEntitlementsFor === pack.id && (
                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden border-t border-zinc-800">
                    <EntitlementsManager adminPwd={adminPwd} pack={pack} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ────────── Pack create/edit form ────────── */
function AlbumPackForm({ adminPwd, pack, onClose, onSaved }) {
  const isEdit = !!pack;
  const [form, setForm] = useState({
    name: pack?.name || '',
    artist: pack?.artist || '',
    description: pack?.description || '',
    price: pack?.price ?? 9.99,
    cover_image: pack?.cover_image || '',
    active: pack?.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const body = { password: adminPwd, ...form, price: Number(form.price) || 0 };
      const url = isEdit ? `${API}/admin/album-packs/${pack.id}` : `${API}/admin/album-packs`;
      const r = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Erreur');
      onSaved(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
      className="p-4 bg-zinc-950 border border-yellow-500/20 rounded-xl space-y-3"
      data-testid="album-pack-form">
      <h3 className="text-white font-bold text-lg">{isEdit ? 'Modifier le pack' : 'Nouveau pack'}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          required
          placeholder="Nom du pack (ex: PAPYON ELECTRO Stems)"
          value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })}
          className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
          data-testid="album-pack-name-input"
        />
        <input
          placeholder="Artiste"
          value={form.artist}
          onChange={e => setForm({ ...form, artist: e.target.value })}
          className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
          data-testid="album-pack-artist-input"
        />
        <input
          required
          type="number"
          min="0"
          step="0.01"
          placeholder="Prix EUR"
          value={form.price}
          onChange={e => setForm({ ...form, price: e.target.value })}
          className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
          data-testid="album-pack-price-input"
        />
        <input
          placeholder="URL image de couverture (optionnel)"
          value={form.cover_image}
          onChange={e => setForm({ ...form, cover_image: e.target.value })}
          className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
          data-testid="album-pack-cover-input"
        />
      </div>
      <textarea
        rows="3"
        placeholder="Description"
        value={form.description}
        onChange={e => setForm({ ...form, description: e.target.value })}
        className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
        data-testid="album-pack-description-input"
      />
      <label className="flex items-center gap-2 text-sm text-zinc-400">
        <input
          type="checkbox"
          checked={form.active}
          onChange={e => setForm({ ...form, active: e.target.checked })}
          className="accent-yellow-500"
          data-testid="album-pack-active-checkbox"
        />
        Pack actif (visible dans la Boutique)
      </label>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={saving} data-testid="album-pack-save-button"
          className="px-4 py-2 bg-yellow-500 text-black font-bold rounded-lg flex items-center gap-2 hover:bg-yellow-400 disabled:opacity-50">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {isEdit ? 'Enregistrer' : 'Créer le pack'}
        </button>
        <button type="button" onClick={onClose} className="px-4 py-2 bg-zinc-800 text-zinc-300 rounded-lg flex items-center gap-2 hover:bg-zinc-700">
          <X size={16} /> Annuler
        </button>
      </div>
    </motion.form>
  );
}

/* ────────── Stems manager (per pack) ────────── */
function StemsManager({ adminPwd, pack, onCountChange }) {
  const [stems, setStems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: '', slot: 'free', bpm: '', key: '' });
  const fileRef = useRef(null);

  const fetchStems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/album-packs/${pack.id}/stems?password=${encodeURIComponent(adminPwd)}`);
      if (r.ok) {
        const data = await r.json();
        setStems(Array.isArray(data) ? data : []);
      } else {
        setStems([]);
      }
    } catch {
      setStems([]);
    } finally {
      setLoading(false);
    }
  }, [adminPwd, pack.id]);

  useEffect(() => { fetchStems(); }, [fetchStems]);

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { alert('Selectionne un fichier audio'); return; }
    if (!form.name.trim()) { alert('Nom du stem requis'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('admin_password', adminPwd);
      fd.append('name', form.name);
      fd.append('slot', form.slot || 'free');
      fd.append('bpm', String(form.bpm || 0));
      fd.append('key', form.key || '');
      fd.append('sort_order', String(stems.length));
      fd.append('file', file);
      const r = await fetch(`${API}/admin/album-packs/${pack.id}/stems`, { method: 'POST', body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Erreur upload');
      const newStems = [...stems, data];
      setStems(newStems);
      onCountChange?.(newStems.length);
      setForm({ name: '', slot: 'free', bpm: '', key: '' });
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteStem = async (stem) => {
    if (!window.confirm(`Supprimer le stem "${stem.name}" ?`)) return;
    const r = await fetch(`${API}/admin/album-packs/stems/${stem.id}?password=${encodeURIComponent(adminPwd)}`, { method: 'DELETE' });
    if (r.ok || r.status === 204) {
      const newStems = stems.filter(s => s.id !== stem.id);
      setStems(newStems);
      onCountChange?.(newStems.length);
    } else {
      alert('Erreur lors de la suppression du stem');
    }
  };

  return (
    <div className="p-4 space-y-4 bg-black/40">
      <form onSubmit={handleUpload} className="space-y-2" data-testid={`stems-upload-form-${pack.id}`}>
        <p className="text-xs text-yellow-400 font-bold uppercase tracking-wider">Ajouter un stem (WAV/MP3 — max 25 Mo)</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <input required placeholder="Nom (ex: Drums)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            className="px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-white text-sm col-span-2" data-testid={`stem-name-input-${pack.id}`} />
          <select value={form.slot} onChange={e => setForm({ ...form, slot: e.target.value })}
            className="px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-white text-sm">
            <option value="free">Libre</option>
            <option value="drums">Drums</option>
            <option value="bass">Bass</option>
            <option value="vocals">Vocals</option>
            <option value="synths">Synths</option>
            <option value="fx">FX</option>
            <option value="other">Autre</option>
          </select>
          <input type="number" min="40" max="240" placeholder="BPM" value={form.bpm} onChange={e => setForm({ ...form, bpm: e.target.value })}
            className="px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-white text-sm" />
          <input placeholder="Tonalité (ex: Am)" value={form.key} onChange={e => setForm({ ...form, key: e.target.value })}
            className="px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-white text-sm" />
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="audio/*" required className="text-zinc-400 text-sm flex-1" data-testid={`stem-file-input-${pack.id}`} />
          <button type="submit" disabled={uploading} data-testid={`stem-upload-button-${pack.id}`}
            className="px-3 py-1.5 bg-yellow-500 text-black font-bold rounded text-sm flex items-center gap-1 hover:bg-yellow-400 disabled:opacity-50">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
          </button>
        </div>
      </form>

      <div className="space-y-1">
        <p className="text-xs text-zinc-500 uppercase tracking-wider">{stems.length} stems</p>
        {loading ? (
          <p className="text-zinc-500 text-sm">Chargement…</p>
        ) : stems.length === 0 ? (
          <p className="text-zinc-600 text-sm italic">Aucun stem pour ce pack.</p>
        ) : (
          stems.map(stem => (
            <div key={stem.id} className="flex items-center gap-3 p-2 rounded bg-zinc-900/60 border border-zinc-800">
              <Music size={14} className="text-yellow-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold truncate">{stem.name}</p>
                <p className="text-zinc-500 text-xs truncate">
                  {stem.slot || 'free'}
                  {stem.bpm ? ` • ${stem.bpm} BPM` : ''}
                  {stem.key ? ` • ${stem.key}` : ''}
                  {' • '}{((stem.size_bytes || 0) / 1024 / 1024).toFixed(2)} Mo
                </p>
              </div>
              <audio controls src={`${process.env.REACT_APP_BACKEND_URL}${stem.url}`} className="h-7 max-w-[160px]" />
              <button onClick={() => handleDeleteStem(stem)} data-testid={`stem-delete-${stem.id}`} className="text-zinc-400 hover:text-red-400 p-1">
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ────────── Entitlements manager (per pack) ────────── */
function EntitlementsManager({ adminPwd, pack }) {
  const [ents, setEnts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [granting, setGranting] = useState(false);

  const fetchEnts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/album-packs/entitlements?password=${encodeURIComponent(adminPwd)}&pack_id=${pack.id}`);
      const data = await r.json();
      setEnts(data.entitlements || []);
    } catch {
      setEnts([]);
    } finally {
      setLoading(false);
    }
  }, [adminPwd, pack.id]);

  useEffect(() => { fetchEnts(); }, [fetchEnts]);

  const handleGrant = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) { alert('Email invalide'); return; }
    setGranting(true);
    try {
      const r = await fetch(`${API}/admin/album-packs/grant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPwd, email, album_pack_id: pack.id }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Erreur');
      if (data.status === 'already_owned') {
        alert('Cet email possède déjà ce pack');
      } else {
        await fetchEnts();
      }
      setEmail('');
    } catch (err) {
      alert(err.message);
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async (ent) => {
    if (!window.confirm(`Revoquer l'acces de ${ent.email} ?`)) return;
    const r = await fetch(`${API}/admin/album-packs/entitlements/${ent.id}?password=${encodeURIComponent(adminPwd)}`, { method: 'DELETE' });
    if (r.ok || r.status === 204) {
      setEnts(ents.filter(e => e.id !== ent.id));
    } else {
      alert('Erreur lors de la revocation');
    }
  };

  return (
    <div className="p-4 space-y-4 bg-black/40">
      <form onSubmit={handleGrant} className="flex items-center gap-2" data-testid={`entitlement-grant-form-${pack.id}`}>
        <Mail size={14} className="text-emerald-400" />
        <input
          type="email"
          placeholder="email@exemple.com — accorder manuellement"
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="flex-1 px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-white text-sm"
          data-testid={`entitlement-email-input-${pack.id}`}
        />
        <button type="submit" disabled={granting} data-testid={`entitlement-grant-button-${pack.id}`}
          className="px-3 py-1.5 bg-emerald-500 text-black font-bold rounded text-sm flex items-center gap-1 hover:bg-emerald-400 disabled:opacity-50">
          {granting ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Accorder
        </button>
      </form>
      <div className="space-y-1">
        <p className="text-xs text-zinc-500 uppercase tracking-wider">{ents.length} acheteurs</p>
        {loading ? (
          <p className="text-zinc-500 text-sm">Chargement…</p>
        ) : ents.length === 0 ? (
          <p className="text-zinc-600 text-sm italic">Aucun acheteur pour le moment.</p>
        ) : (
          ents.map(ent => (
            <div key={ent.id} className="flex items-center gap-3 p-2 rounded bg-zinc-900/60 border border-zinc-800">
              <Check size={14} className="text-emerald-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold truncate">{ent.email}</p>
                <p className="text-zinc-500 text-xs truncate">
                  {ent.paypal_order_id === 'admin_grant' ? 'Acces manuel' : `Order ${ent.paypal_order_id}`}
                  {' • '}{new Date(ent.granted_at).toLocaleString('fr-FR')}
                </p>
              </div>
              <button onClick={() => handleRevoke(ent)} data-testid={`entitlement-revoke-${ent.id}`} className="text-zinc-400 hover:text-red-400 p-1">
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
